(() => {
  "use strict";

  /* =======================================================
     DISTANT TRAVELER — VOICE ARCHIVE
     CLEAN MASTER JS

     FEATURES
     - Dedicated Journal audio player
     - Physical PLAY / STOP / PAUSE / REW / FWD controls
     - Mechanical button click sounds
     - Cassette reels tied to real audio time
     - Organic audio-reactive waveform
     - Timed transcript alignment JSON
     - Five draggable Polaroids per log
     - Recent transmission switching + autoplay
     - Slide-down archive drawer
     - Year / month / week / day archive navigation
  ======================================================= */

  const root = document.querySelector(
    "[data-dt-voice-journal]"
  );

  if (!root) return;

  if (root.dataset.voiceReady === "true") {
    return;
  }

  root.dataset.voiceReady = "true";


  /* =======================================================
     VOICE LOG ROWS
  ======================================================= */

  const rows = Array.from(
    root.querySelectorAll(
      "[data-voice-row]"
    )
  );

  if (!rows.length) return;


  /* =======================================================
     RECORDER
  ======================================================= */

  const recorder = root.querySelector(
    "[data-voice-recorder]"
  );

  const leftRecorderReel = root.querySelector(
    ".dt-voice-reel-left"
  );

  const rightRecorderReel = root.querySelector(
    ".dt-voice-reel-right"
  );

  const playButton = root.querySelector(
    '[data-voice-control="play"]'
  );

  const stopButton = root.querySelector(
    '[data-voice-control="stop"]'
  );

  const pauseButton = root.querySelector(
    '[data-voice-control="pause"]'
  );

  const rewindButton = root.querySelector(
    '[data-voice-control="rew"]'
  );

  const forwardButton = root.querySelector(
    '[data-voice-control="fwd"]'
  );


  /* =======================================================
     CURRENT LOG
  ======================================================= */

  const selectedDate = root.querySelector(
    "[data-voice-selected-date]"
  );

  const selectedTitle = root.querySelector(
    "[data-voice-selected-title]"
  );

  const selectedLog = root.querySelector(
    "[data-voice-selected-log]"
  );

  const transcript = root.querySelector(
    "[data-voice-transcript]"
  );

  const currentTimeLabel = root.querySelector(
    "[data-voice-current-time]"
  );

  const durationLabel = root.querySelector(
    "[data-voice-duration]"
  );


  /* =======================================================
     RECENT TRANSMISSIONS
  ======================================================= */

  const recentItems = Array.from(
    root.querySelectorAll(
      "[data-recent-log]"
    )
  );


  const topToggleButton = root.querySelector(
    "[data-voice-top-toggle]"
  );

  const topToggleIcon = root.querySelector(
    "[data-voice-top-toggle-icon]"
  );

  const recentStrip = root.querySelector(
    "[data-recent-strip]"
  );

  const recentPrevButton = root.querySelector(
    "[data-recent-prev]"
  );

  const recentNextButton = root.querySelector(
    "[data-recent-next]"
  );


  /* =======================================================
     WAVEFORM
  ======================================================= */

  const liveWaveBars = Array.from(
    root.querySelectorAll(
      ".dt-voice-live-wave i"
    )
  );


  /* =======================================================
     POLAROIDS
  ======================================================= */

  const polaroidStage = root.querySelector(
    "[data-voice-polaroid-stage]"
  );

  const polaroidStack = root.querySelector(
    "[data-voice-polaroid-stack]"
  );

  const photoEmpty = root.querySelector(
    "[data-voice-photo-empty]"
  );

  const photoCounter = root.querySelector(
    "[data-voice-photo-counter]"
  );


  /* =======================================================
     ARCHIVE DRAWER
  ======================================================= */

  const archiveScrim = root.querySelector(
    "[data-archive-scrim]"
  );

  const archiveDrawer = root.querySelector(
    "[data-archive-drawer]"
  );

  const archiveOpenButtons = Array.from(
    root.querySelectorAll(
      "[data-archive-open]"
    )
  );

  const archiveCloseButton = root.querySelector(
    "[data-archive-close]"
  );

  const archiveTabs = Array.from(
    root.querySelectorAll(
      "[data-archive-tab]"
    )
  );

  const archiveYears = root.querySelector(
    "[data-archive-years]"
  );

  const calendar = root.querySelector(
    "[data-voice-calendar]"
  );

  const yearLabel = root.querySelector(
    "[data-voice-year-label]"
  );

  const previousYearButton = root.querySelector(
    "[data-voice-prev-year]"
  );

  const nextYearButton = root.querySelector(
    "[data-voice-next-year]"
  );

  const archiveMonthTitle = root.querySelector(
    "[data-archive-month-title]"
  );

  const archiveMonthMeta = root.querySelector(
    "[data-archive-month-meta]"
  );

  const archiveWeeks = root.querySelector(
    "[data-archive-weeks]"
  );

  const archiveDays = root.querySelector(
    "[data-archive-days]"
  );

  const monthEmpty = root.querySelector(
    "[data-voice-month-empty]"
  );

  const archiveLoadButton = root.querySelector(
    "[data-archive-load-log]"
  );


  /* =======================================================
     AUDIO
  ======================================================= */

  const audio = new Audio();

  audio.preload = "metadata";
  audio.playsInline = true;
  audio.crossOrigin = "anonymous";


  /* =======================================================
     STATE
  ======================================================= */

  let selectedRow =
    rows.find((row) =>
      row.classList.contains(
        "is-selected"
      )
    ) || rows[0];

  let archiveSelectionRow = selectedRow;

  let recorderMode = "stopped";

  const initialDate =
    parseDate(
      selectedRow.dataset.date
    ) || new Date();

  let currentYear =
    initialDate.getFullYear();

  let selectedMonth =
    initialDate.getMonth();

  const availableYears = Array.from(
    new Set(
      rows
        .map((row) =>
          parseDate(
            row.dataset.date
          )
        )
        .filter(Boolean)
        .map((date) =>
          date.getFullYear()
        )
    )
  ).sort((a, b) => a - b);


  /* =======================================================
     HELPERS
  ======================================================= */

  function numberValue(
    value,
    fallback = 0
  ) {
    const parsed =
      Number.parseFloat(value);

    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  }


  function clamp(
    value,
    minimum,
    maximum
  ) {
    return Math.min(
      maximum,
      Math.max(
        minimum,
        value
      )
    );
  }


  function parseDate(value) {
    if (!value) return null;

    const parts =
      value
        .split("-")
        .map(Number);

    if (
      parts.length !== 3 ||
      parts.some(
        (part) =>
          !Number.isFinite(part)
      )
    ) {
      return null;
    }

    return new Date(
      parts[0],
      parts[1] - 1,
      parts[2],
      12,
      0,
      0
    );
  }


  function formatLongDate(value) {
    const date = parseDate(value);

    if (!date) return "";

    return new Intl.DateTimeFormat(
      "en-US",
      {
        month: "long",
        day: "2-digit",
        year: "numeric"
      }
    )
      .format(date)
      .toUpperCase();
  }


  function formatTime(seconds) {
    if (
      !Number.isFinite(seconds) ||
      seconds < 0
    ) {
      return "00:00";
    }

    const minutes =
      Math.floor(
        seconds / 60
      );

    const remainingSeconds =
      Math.floor(
        seconds % 60
      );

    return (
      String(minutes)
        .padStart(2, "0") +
      ":" +
      String(remainingSeconds)
        .padStart(2, "0")
    );
  }


  function localDateKey(date) {
    const year =
      date.getFullYear();

    const month =
      String(
        date.getMonth() + 1
      ).padStart(2, "0");

    const day =
      String(
        date.getDate()
      ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }


  /* =======================================================
     MECHANICAL CASSETTE BUTTON SOUND
  ======================================================= */

  let cassetteClickContext = null;


  function playCassetteClick(
    type = "button"
  ) {
    const AudioContextClass =
      window.AudioContext ||
      window.webkitAudioContext;

    if (!AudioContextClass) {
      return;
    }

    if (!cassetteClickContext) {
      cassetteClickContext =
        new AudioContextClass();
    }

    const context =
      cassetteClickContext;

    if (
      context.state ===
      "suspended"
    ) {
      context.resume().catch(() => {});
    }

    const now =
      context.currentTime;

    const length =
      Math.floor(
        context.sampleRate *
        0.026
      );

    const buffer =
      context.createBuffer(
        1,
        length,
        context.sampleRate
      );

    const data =
      buffer.getChannelData(0);

    for (
      let i = 0;
      i < length;
      i += 1
    ) {
      const decay =
        1 - i / length;

      data[i] =
        (
          Math.random() * 2 - 1
        ) *
        decay *
        decay;
    }

    const source =
      context.createBufferSource();

    source.buffer = buffer;

    const filter =
      context.createBiquadFilter();

    filter.type = "bandpass";

    filter.frequency.value =
      type === "stop"
        ? 950
        : 1450;

    filter.Q.value = 1.4;

    const clickGain =
      context.createGain();

    clickGain.gain
      .setValueAtTime(
        0.052,
        now
      );

    clickGain.gain
      .exponentialRampToValueAtTime(
        0.001,
        now + 0.032
      );

    source.connect(filter);
    filter.connect(clickGain);
    clickGain.connect(
      context.destination
    );

    source.start(now);
    source.stop(now + 0.035);

    const thunk =
      context.createOscillator();

    thunk.type = "triangle";

    thunk.frequency
      .setValueAtTime(
        type === "stop"
          ? 72
          : 96,
        now
      );

    thunk.frequency
      .exponentialRampToValueAtTime(
        type === "stop"
          ? 48
          : 62,
        now + 0.05
      );

    const thunkGain =
      context.createGain();

    thunkGain.gain
      .setValueAtTime(
        type === "stop"
          ? 0.042
          : 0.03,
        now
      );

    thunkGain.gain
      .exponentialRampToValueAtTime(
        0.001,
        now + 0.055
      );

    thunk.connect(thunkGain);
    thunkGain.connect(
      context.destination
    );

    thunk.start(now);
    thunk.stop(now + 0.06);
  }


 /* =======================================================
   CASSETTE REELS
======================================================= */

let recorderReelFrame = null;

function renderRecorderReels() {
  if (!leftRecorderReel || !rightRecorderReel) {
    return;
  }

  const angle = (audio.currentTime * 160) % 360;

  leftRecorderReel.style.transform =
    `translate(-50%, -50%) rotate(${-angle}deg)`;

  rightRecorderReel.style.transform =
    `translate(-50%, -50%) rotate(${angle}deg)`;
}

function drawRecorderReels() {
  renderRecorderReels();

  if (!audio.paused && !audio.ended) {
    recorderReelFrame = window.requestAnimationFrame(drawRecorderReels);
  } else {
    recorderReelFrame = null;
  }
}

function startRecorderReels() {
  if (!leftRecorderReel || !rightRecorderReel) return;
  if (recorderReelFrame !== null) return;

  recorderReelFrame = window.requestAnimationFrame(drawRecorderReels);
}

function stopRecorderReels() {
  if (recorderReelFrame !== null) {
    window.cancelAnimationFrame(recorderReelFrame);
    recorderReelFrame = null;
  }

  renderRecorderReels();
}
  /* =======================================================
     REAL AUDIO-REACTIVE WAVEFORM
  ======================================================= */

  let voiceAudioContext = null;
  let voiceAudioSource = null;
  let voiceAnalyser = null;
  let voiceTimeData = null;
  let voiceFrequencyData = null;
  let voiceWaveFrame = null;
  let voiceWaveReady = false;

  const voiceWaveLevels =
    liveWaveBars.map(() => 0);


  async function ensureVoiceAnalyser() {
    if (voiceWaveReady) {
      if (
        voiceAudioContext &&
        voiceAudioContext.state ===
          "suspended"
      ) {
        try {
          await voiceAudioContext.resume();
        } catch (_) {}
      }

      return true;
    }

    const AudioContextClass =
      window.AudioContext ||
      window.webkitAudioContext;

    if (
      !AudioContextClass ||
      !liveWaveBars.length
    ) {
      return false;
    }

    try {
      voiceAudioContext =
        new AudioContextClass();

      voiceAudioSource =
        voiceAudioContext
          .createMediaElementSource(
            audio
          );

      voiceAnalyser =
        voiceAudioContext
          .createAnalyser();

      voiceAnalyser.fftSize = 512;
      voiceAnalyser.smoothingTimeConstant = 0.56;

      voiceTimeData =
        new Uint8Array(
          voiceAnalyser.fftSize
        );

      voiceFrequencyData =
        new Uint8Array(
          voiceAnalyser.frequencyBinCount
        );

      voiceAudioSource.connect(
        voiceAnalyser
      );

      voiceAnalyser.connect(
        voiceAudioContext.destination
      );

      voiceWaveReady = true;

      if (
        voiceAudioContext.state ===
          "suspended"
      ) {
        await voiceAudioContext.resume();
      }

      return true;
    } catch (error) {
      console.warn(
        "Voice waveform analyser could not start:",
        error
      );

      return false;
    }
  }


  function updateWaveBarStyle(
    bar,
    level,
    index
  ) {
    if (!bar) return;

    const shaped =
      Math.pow(
        clamp(level, 0, 1),
        0.86
      );

    const height =
      5 + shaped * 58;

    const position =
      liveWaveBars.length > 1
        ? index /
          (
            liveWaveBars.length - 1
          )
        : 0;

    const hue =
      32 +
      Math.sin(
        position *
        Math.PI *
        2.3
      ) * 2.4;

    const opacity =
      0.18 + shaped * 0.80;

    const glow =
      Math.max(
        0,
        (shaped - 0.22) * 13
      );

    const glowAlpha =
      Math.max(
        0,
        (shaped - 0.20) * 0.34
      );

    bar.style.setProperty(
      "--wave-height",
      `${height.toFixed(1)}px`
    );

    bar.style.setProperty(
      "--wave-hue",
      hue.toFixed(1)
    );

    bar.style.setProperty(
      "--wave-opacity",
      opacity.toFixed(3)
    );

    bar.style.setProperty(
      "--wave-glow",
      `${glow.toFixed(1)}px`
    );

    bar.style.setProperty(
      "--wave-glow-alpha",
      glowAlpha.toFixed(3)
    );
  }


  function drawVoiceWave() {
    if (
      !voiceAnalyser ||
      !voiceTimeData ||
      !voiceFrequencyData ||
      !liveWaveBars.length
    ) {
      voiceWaveFrame = null;
      return;
    }

    voiceAnalyser
      .getByteTimeDomainData(
        voiceTimeData
      );

    voiceAnalyser
      .getByteFrequencyData(
        voiceFrequencyData
      );

    let energySum = 0;

    for (
      let i = 0;
      i < voiceTimeData.length;
      i += 1
    ) {
      const normalized =
        (
          voiceTimeData[i] -
          128
        ) / 128;

      energySum +=
        normalized *
        normalized;
    }

    const rms =
      Math.sqrt(
        energySum /
        voiceTimeData.length
      );

    const voiceEnergy =
      Math.pow(
        clamp(
          (rms - 0.008) * 15,
          0,
          1
        ),
        0.82
      );

    const maxUsefulBin =
      Math.min(
        voiceFrequencyData.length - 1,
        110
      );

    liveWaveBars.forEach(
      (bar, index) => {
        const ratio =
          liveWaveBars.length > 1
            ? index /
              (
                liveWaveBars.length - 1
              )
            : 0;

        const curvedRatio =
          Math.pow(
            ratio,
            1.28
          );

        const centerBin =
          Math.floor(
            2 +
            curvedRatio *
            (maxUsefulBin - 2)
          );

        let localSum = 0;
        let localWeight = 0;

        for (
          let offset = -2;
          offset <= 2;
          offset += 1
        ) {
          const bin =
            clamp(
              centerBin + offset,
              0,
              maxUsefulBin
            );

          const weight =
            offset === 0
              ? 1
              : Math.abs(offset) === 1
              ? 0.72
              : 0.42;

          localSum +=
            (
              voiceFrequencyData[bin] /
              255
            ) * weight;

          localWeight += weight;
        }

        const localFrequency =
          localWeight > 0
            ? localSum /
              localWeight
            : 0;

        const organicContour =
          0.82 +
          (
            Math.sin(
              ratio *
              Math.PI *
              5.2
            ) + 1
          ) * 0.065 +
          (
            Math.sin(
              ratio *
              Math.PI *
              11.6
            ) + 1
          ) * 0.025;

        let target =
          Math.pow(
            clamp(
              localFrequency * 1.85,
              0,
              1
            ),
            0.78
          ) * 0.72 +
          voiceEnergy * 0.38;

        target *=
          organicContour;

        if (rms < 0.012) {
          target *= 0.12;
        }

        target =
          clamp(
            target,
            0,
            1
          );

        const previous =
          voiceWaveLevels[index] || 0;

        const smoothing =
          target > previous
            ? 0.56
            : 0.16;

        const smoothed =
          previous +
          (
            target - previous
          ) * smoothing;

        voiceWaveLevels[index] =
          smoothed;

        updateWaveBarStyle(
          bar,
          smoothed,
          index
        );
      }
    );

    syncTimedTranscript(
      audio.currentTime
    );

    voiceWaveFrame =
      window.requestAnimationFrame(
        drawVoiceWave
      );
  }


  function startVoiceWave() {
    if (
      voiceWaveFrame !== null
    ) {
      return;
    }

    voiceWaveFrame =
      window.requestAnimationFrame(
        drawVoiceWave
      );
  }


  function settleVoiceWave() {
    liveWaveBars.forEach(
      (bar, index) => {
        const idle =
          0.003 +
          (
            Math.sin(
              index * 1.7
            ) + 1
          ) * 0.003;

        voiceWaveLevels[index] =
          idle;

        updateWaveBarStyle(
          bar,
          idle,
          index
        );
      }
    );
  }


  function stopVoiceWave() {
    if (
      voiceWaveFrame !== null
    ) {
      window.cancelAnimationFrame(
        voiceWaveFrame
      );

      voiceWaveFrame = null;
    }

    settleVoiceWave();
  }
    /* =======================================================
     TIMED LIVE TRANSCRIPT
  ======================================================= */

  let activeTranscriptLines = [];
  let activeTranscriptIndex = -1;


  function getTranscriptAlignment(row) {
    if (!row) return [];

    const script =
      row.querySelector(
        "[data-voice-row-alignment]"
      );

    if (!script) return [];

    const raw =
      script.textContent.trim();

    if (!raw) return [];

    try {
      let parsed =
        JSON.parse(raw);

      if (
        typeof parsed ===
        "string"
      ) {
        parsed =
          JSON.parse(parsed);
      }

      const lines =
        Array.isArray(parsed)
          ? parsed
          : parsed?.lines;

      if (!Array.isArray(lines)) {
        return [];
      }

      return lines
        .map((line) => ({
          id: line?.id,

          start:
            Number(line?.start),

          end:
            Number(line?.end),

          text:
            String(
              line?.text || ""
            ).trim(),

          mode:
            String(
              line?.mode ||
              "spoken"
            )
              .trim()
              .toLowerCase()
        }))
        .filter((line) =>
          Number.isFinite(
            line.start
          ) &&
          Boolean(line.text)
        )
        .sort(
          (a, b) =>
            a.start - b.start
        );

    } catch (error) {

      console.warn(
        "Transcript Alignment JSON could not be read:",
        error
      );

      return [];
    }
  }


  function renderTranscriptLine(line) {
    if (!transcript) return;

    transcript.textContent = "";

    const paragraph =
      document.createElement(
        "p"
      );

    paragraph.className =
      "dt-voice-live-line";

    if (line?.mode) {
      paragraph.dataset.mode =
        line.mode;
    }

    paragraph.textContent =
      line?.text || "...";

    transcript.appendChild(
      paragraph
    );

    transcript.dataset.mode =
      line?.mode || "";
  }


  function syncTimedTranscript(
    seconds,
    force = false
  ) {
    if (
      !transcript ||
      !activeTranscriptLines.length
    ) {
      return;
    }

    let lineIndex = 0;

    for (
      let i = 0;
      i < activeTranscriptLines.length;
      i += 1
    ) {
      if (
        seconds >=
        activeTranscriptLines[i].start
      ) {
        lineIndex = i;
      } else {
        break;
      }
    }

    if (
      !force &&
      lineIndex ===
        activeTranscriptIndex
    ) {
      return;
    }

    activeTranscriptIndex =
      lineIndex;

    renderTranscriptLine(
      activeTranscriptLines[
        lineIndex
      ]
    );
  }


  function updateTranscript(row) {
    if (
      !row ||
      !transcript
    ) {
      return;
    }

    activeTranscriptLines =
      getTranscriptAlignment(
        row
      );

    activeTranscriptIndex =
      -1;

    if (
      activeTranscriptLines.length
    ) {
      syncTimedTranscript(
        0,
        true
      );

      return;
    }

    const lyrics =
      row.querySelector(
        "[data-voice-row-lyrics]"
      );

    if (lyrics) {
      transcript.innerHTML =
        lyrics.innerHTML;

      transcript.dataset.mode =
        "";

      return;
    }

    transcript.textContent =
      row.dataset.transcript ||
      "NO TRANSCRIPT AVAILABLE.";

    transcript.dataset.mode =
      "";
  }


  /* =======================================================
     RECORDER STATES
  ======================================================= */

  function hit(button) {
    if (!button) return;

    button.classList.add(
      "is-hit"
    );

    window.setTimeout(
      () => {
        button.classList.remove(
          "is-hit"
        );
      },
      150
    );
  }


  function syncTopToggle() {
    if (!topToggleButton) return;

    const playing =
      recorderMode === "playing" &&
      !audio.paused &&
      !audio.ended;

    topToggleButton.classList.toggle(
      "is-playing",
      playing
    );

    topToggleButton.setAttribute(
      "aria-label",
      playing
        ? "Pause recording"
        : "Play recording"
    );

    if (topToggleIcon) {
      topToggleIcon.textContent =
        playing ? "Ⅱ" : "▶";
    }
  }


  function clearLocks() {
    playButton?.classList.remove(
      "is-locked"
    );

    pauseButton?.classList.remove(
      "is-locked"
    );
  }


  function setPlayingState() {
    recorderMode = "playing";

    clearLocks();

    root.classList.add(
      "is-playing"
    );

    root.classList.remove(
      "is-paused"
    );

    recorder?.classList.add(
      "is-playing"
    );

    recorder?.classList.remove(
      "is-paused"
    );

    playButton?.classList.add(
      "is-locked"
    );

    syncTopToggle();
  }


  function setPausedState() {
    recorderMode = "paused";

    clearLocks();

    root.classList.remove(
      "is-playing"
    );

    root.classList.add(
      "is-paused"
    );

    recorder?.classList.remove(
      "is-playing"
    );

    recorder?.classList.add(
      "is-paused"
    );

    pauseButton?.classList.add(
      "is-locked"
    );

    syncTopToggle();
  }


  function setStoppedState() {
    recorderMode = "stopped";

    clearLocks();

    root.classList.remove(
      "is-playing"
    );

    root.classList.remove(
      "is-paused"
    );

    recorder?.classList.remove(
      "is-playing"
    );

    recorder?.classList.remove(
      "is-paused"
    );

    syncTopToggle();
  }


  /* =======================================================
     AUDIO HELPERS
  ======================================================= */

  function sourceForRow(row) {
    return (
      row?.dataset.audio ||
      ""
    ).trim();
  }


  function pauseSiteMusic() {
    const siteAudio =
      window
        .DistantTravelerPlayer
        ?.audio;

    if (
      siteAudio instanceof
        HTMLAudioElement &&
      siteAudio !== audio &&
      !siteAudio.paused
    ) {
      siteAudio.pause();
    }
  }


  function setControlsEnabled(
    enabled
  ) {
    [
      playButton,
      stopButton,
      pauseButton,
      rewindButton,
      forwardButton,
      topToggleButton
    ].forEach((button) => {

      if (button) {
        button.disabled =
          !enabled;
      }

    });
  }


  function stopAudio(
    reset = true
  ) {
    audio.pause();

    stopVoiceWave();
    stopRecorderReels();

    if (reset) {

      try {
        audio.currentTime = 0;
      } catch (_) {}

      renderRecorderReels();

      if (currentTimeLabel) {
        currentTimeLabel.textContent =
          "00:00";
      }

      if (
        activeTranscriptLines.length
      ) {
        syncTimedTranscript(
          0,
          true
        );
      }
    }

    setStoppedState();
  }


  function loadAudioForRow(row) {
    if (!row) return;

    stopAudio(true);

    const source =
      sourceForRow(row);

    if (!source) {

      audio.removeAttribute(
        "src"
      );

      try {
        audio.load();
      } catch (_) {}

      setControlsEnabled(
        false
      );

      if (durationLabel) {
        durationLabel.textContent =
          row.dataset.duration ||
          "--:--";
      }

      return;
    }

    setControlsEnabled(
      true
    );

    audio.src = source;

    audio.load();

    if (durationLabel) {
      durationLabel.textContent =
        row.dataset.duration ||
        "--:--";
    }
  }


  /* =======================================================
     POLAROID DATA
  ======================================================= */

  function getPhotosForRow(row) {
    if (!row) return [];

    const photoData =
      row.querySelector(
        "[data-voice-row-photos-data]"
      );

    if (!photoData) return [];

    return Array.from(
      photoData.querySelectorAll(
        "[data-voice-photo]"
      )
    )
      .map(
        (item, index) => ({
          src:
            (
              item.dataset.src ||
              ""
            ).trim(),

          alt:
            item.dataset.alt ||
            `Journal photograph ${
              index + 1
            }`,

          originalIndex:
            index
        })
      )
      .filter(
        (photo) =>
          Boolean(photo.src)
      )
      .slice(
        0,
        5
      );
  }


  /* =======================================================
     POLAROID STATE
  ======================================================= */

  const polaroidMemory =
    new Map();

  let polaroidTopZ =
    100;


  const desktopPolaroidLayouts = [
  /* FRONT / HERO */
  {
    x: 8,
    y: 34,
    rotation: -1.5,
    z: 50
  },

  /* BACK LEFT */
  {
    x: -108,
    y: -40,
    rotation: -7.5,
    z: 24
  },

  /* BACK RIGHT */
  {
    x: 104,
    y: -52,
    rotation: 6,
    z: 32
  },

  /* LOWER LEFT */
  {
    x: -82,
    y: 82,
    rotation: -4.5,
    z: 18
  },

  /* LOWER RIGHT */
  {
    x: 88,
    y: 72,
    rotation: 5.5,
    z: 14
  }
];


  const mobilePolaroidLayouts = [
    {
      x: 0,
      y: 0,
      rotation: -2,
      z: 50
    },

    {
      x: 34,
      y: -10,
      rotation: 6,
      z: 40
    },

    {
      x: -36,
      y: 8,
      rotation: -7,
      z: 30
    },

    {
      x: 47,
      y: 26,
      rotation: 10,
      z: 20
    },

    {
      x: -48,
      y: 29,
      rotation: -9,
      z: 10
    }
  ];


  function getPolaroidNoteKey(row) {
    if (!row) {
      return "default";
    }

    return [
      row.dataset.date || "",
      row.dataset.log || "",
      row.dataset.title || ""
    ].join(
      "::"
    );
  }


  function applyPolaroidTransform(
    card,
    dragging = false
  ) {
    if (!card) return;

    const x =
      numberValue(
        card.dataset.x
      );

    const y =
      numberValue(
        card.dataset.y
      );

    const rotation =
      numberValue(
        card.dataset.rotation
      );

    const scale =
      dragging
        ? 1.045
        : 1;

    card.style.transform = `
      translate(
        calc(-50% + ${x}px),
        calc(-50% + ${y}px)
      )
      rotate(${rotation}deg)
      scale(${scale})
    `;
  }


  function constrainPolaroid(
    card,
    x,
    y
  ) {
    if (
      !polaroidStage ||
      !card
    ) {
      return {
        x,
        y
      };
    }

    const stageWidth =
      polaroidStage.clientWidth;

    const stageHeight =
      polaroidStage.clientHeight;

    const cardWidth =
      card.offsetWidth ||
      250;

    const cardHeight =
      card.offsetHeight ||
      300;

    const visibleX =
      Math.max(
        70,
        cardWidth * 0.28
      );

    const visibleY =
      Math.max(
        70,
        cardHeight * 0.25
      );

    const maxX =
      Math.max(
        0,
        stageWidth / 2 -
        visibleX
      );

    const maxY =
      Math.max(
        0,
        stageHeight / 2 -
        visibleY
      );

    return {
      x:
        clamp(
          x,
          -maxX,
          maxX
        ),

      y:
        clamp(
          y,
          -maxY,
          maxY
        )
    };
  }


  function updatePhotoCounter() {
    if (
      !photoCounter ||
      !polaroidStack
    ) {
      return;
    }

    const cards =
      Array.from(
        polaroidStack.querySelectorAll(
          "[data-polaroid-card]"
        )
      );

    if (!cards.length) {
      photoCounter.textContent =
        "00 / 00";

      return;
    }

    const front =
      cards.find(
        (card) =>
          card.classList.contains(
            "is-front"
          )
      ) ||
      cards[0];

    const index =
      numberValue(
        front.dataset.photoIndex
      );

    photoCounter.textContent =
      `${String(
        index + 1
      ).padStart(
        2,
        "0"
      )} / ${String(
        cards.length
      ).padStart(
        2,
        "0"
      )}`;
  }


  function bringPolaroidToFront(card) {
    if (
      !card ||
      !polaroidStack
    ) {
      return;
    }

    polaroidStack
      .querySelectorAll(
        "[data-polaroid-card]"
      )
      .forEach(
        (candidate) =>
          candidate.classList.remove(
            "is-front"
          )
      );

    polaroidTopZ += 1;

    card.dataset.z =
      String(
        polaroidTopZ
      );

    card.style.zIndex =
      String(
        polaroidTopZ
      );

    card.classList.add(
      "is-front"
    );

    updatePhotoCounter();
  }


  function savePolaroidArrangement(row) {
    if (
      !row ||
      !polaroidStack
    ) {
      return;
    }

    const state =
      Array.from(
        polaroidStack.querySelectorAll(
          "[data-polaroid-card]"
        )
      )
        .map(
          (card) => ({
            photoIndex:
              numberValue(
                card.dataset.photoIndex
              ),

            x:
              numberValue(
                card.dataset.x
              ),

            y:
              numberValue(
                card.dataset.y
              ),

            rotation:
              numberValue(
                card.dataset.rotation
              ),

            z:
              numberValue(
                card.dataset.z
              ),

            front:
              card.classList.contains(
                "is-front"
              )
          })
        );

    polaroidMemory.set(
      getPolaroidNoteKey(row),
      state
    );
  }


  function setInitialPolaroidPosition(
    card,
    index
  ) {
    const isMobile =
      window.matchMedia(
        "(max-width: 749px)"
      ).matches;

    const layouts =
      isMobile
        ? mobilePolaroidLayouts
        : desktopPolaroidLayouts;

    const layout =
      layouts[index] ||
      layouts[
        layouts.length - 1
      ];

    card.dataset.x =
      String(layout.x);

    card.dataset.y =
      String(layout.y);

    card.dataset.rotation =
      String(
        layout.rotation
      );

    card.dataset.z =
      String(layout.z);

    card.style.zIndex =
      String(layout.z);

    card.classList.toggle(
      "is-front",
      index === 0
    );

    applyPolaroidTransform(
      card
    );
  }
    function restorePolaroidArrangement(row) {
    if (
      !row ||
      !polaroidStack
    ) {
      return false;
    }

    const saved =
      polaroidMemory.get(
        getPolaroidNoteKey(row)
      );

    if (!saved) {
      return false;
    }

    const cards =
      Array.from(
        polaroidStack.querySelectorAll(
          "[data-polaroid-card]"
        )
      );

    cards.forEach((card) => {

      const photoIndex =
        numberValue(
          card.dataset.photoIndex
        );

      const stored =
        saved.find(
          (item) =>
            item.photoIndex ===
            photoIndex
        );

      if (!stored) {
        return;
      }

      card.dataset.x =
        String(stored.x);

      card.dataset.y =
        String(stored.y);

      card.dataset.rotation =
        String(
          stored.rotation
        );

      card.dataset.z =
        String(stored.z);

      card.style.zIndex =
        String(stored.z);

      card.classList.toggle(
        "is-front",
        stored.front
      );

      polaroidTopZ =
        Math.max(
          polaroidTopZ,
          stored.z
        );

      applyPolaroidTransform(
        card
      );
    });

    updatePhotoCounter();

    return true;
  }


  function enablePolaroidDrag(
    card,
    row
  ) {
    if (!card) return;

    let dragState = null;

    card.addEventListener(
      "pointerdown",
      (event) => {

        if (
          event.pointerType ===
            "mouse" &&
          event.button !== 0
        ) {
          return;
        }

        event.preventDefault();

        bringPolaroidToFront(
          card
        );

        dragState = {
          pointerId:
            event.pointerId,

          pointerX:
            event.clientX,

          pointerY:
            event.clientY,

          startX:
            numberValue(
              card.dataset.x
            ),

          startY:
            numberValue(
              card.dataset.y
            ),

          startRotation:
            numberValue(
              card.dataset.rotation
            ),

          moved:
            false
        };

        card.classList.add(
          "is-dragging"
        );

        try {
          card.setPointerCapture(
            event.pointerId
          );
        } catch (_) {}

        applyPolaroidTransform(
          card,
          true
        );
      }
    );


    card.addEventListener(
      "pointermove",
      (event) => {

        if (
          !dragState ||
          event.pointerId !==
            dragState.pointerId
        ) {
          return;
        }

        event.preventDefault();

        const deltaX =
          event.clientX -
          dragState.pointerX;

        const deltaY =
          event.clientY -
          dragState.pointerY;

        if (
          Math.hypot(
            deltaX,
            deltaY
          ) > 4
        ) {
          dragState.moved =
            true;
        }

        const position =
          constrainPolaroid(
            card,
            dragState.startX +
              deltaX,
            dragState.startY +
              deltaY
          );

        const rotationChange =
          clamp(
            deltaX *
            0.022,
            -5,
            5
          );

        card.dataset.x =
          String(
            position.x
          );

        card.dataset.y =
          String(
            position.y
          );

        card.dataset.rotation =
          String(
            clamp(
              dragState.startRotation +
                rotationChange,
              -15,
              15
            )
          );

        applyPolaroidTransform(
          card,
          true
        );
      }
    );


    function finishDrag(event) {
      if (
        !dragState ||
        event.pointerId !==
          dragState.pointerId
      ) {
        return;
      }

      const moved =
        dragState.moved;

      card.classList.remove(
        "is-dragging"
      );

      applyPolaroidTransform(
        card,
        false
      );

      try {
        card.releasePointerCapture(
          event.pointerId
        );
      } catch (_) {}

      if (moved) {

        card.dataset.ignoreClick =
          "true";

        window.setTimeout(
          () => {
            card.dataset.ignoreClick =
              "false";
          },
          120
        );
      }

      savePolaroidArrangement(
        row
      );

      dragState = null;
    }


    card.addEventListener(
      "pointerup",
      finishDrag
    );

    card.addEventListener(
      "pointercancel",
      finishDrag
    );

    card.addEventListener(
      "click",
      () => {

        if (
          card.dataset.ignoreClick ===
          "true"
        ) {
          return;
        }

        bringPolaroidToFront(
          card
        );

        savePolaroidArrangement(
          row
        );
      }
    );
  }


  function renderPolaroids(row) {
    if (!polaroidStack) return;

    const photos =
      getPhotosForRow(row);

    polaroidStack.innerHTML =
      "";

    if (!photos.length) {

      polaroidStack.hidden =
        true;

      if (photoEmpty) {
        photoEmpty.hidden =
          false;
      }

      if (photoCounter) {
        photoCounter.textContent =
          "00 / 00";
      }

      return;
    }

    polaroidStack.hidden =
      false;

    if (photoEmpty) {
      photoEmpty.hidden =
        true;
    }

    photos.forEach(
      (photo, index) => {

        const card =
          document.createElement(
            "button"
          );

        card.type =
          "button";

        card.className =
          "dt-voice-polaroid";

        card.dataset.polaroidCard =
          "true";

        card.dataset.photoIndex =
          String(
            photo.originalIndex
          );

        card.setAttribute(
          "aria-label",
          `Move photograph ${
            index + 1
          }`
        );

        const image =
          document.createElement(
            "img"
          );

        image.src =
          photo.src;

        image.alt =
          photo.alt;

        image.draggable =
          false;

        image.loading =
          index === 0
            ? "eager"
            : "lazy";

        card.appendChild(
          image
        );

        polaroidStack.appendChild(
          card
        );

        setInitialPolaroidPosition(
          card,
          index
        );

        enablePolaroidDrag(
          card,
          row
        );
      }
    );

    restorePolaroidArrangement(
      row
    );

    updatePhotoCounter();
  }


  /* =======================================================
     RECENT TRANSMISSIONS
  ======================================================= */

  function syncRecentSelection(row) {
    const rowIndex =
      row?.dataset.rowIndex;

    recentItems.forEach(
      (item) => {

        item.classList.toggle(
          "is-selected",
          item.dataset.rowIndex ===
            rowIndex
        );

      }
    );
  }


  /* =======================================================
     ARCHIVE HELPERS
  ======================================================= */

  const monthNames = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC"
  ];


  const fullMonthNames = [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER"
  ];


  function rowsForMonth(
    year,
    monthIndex
  ) {
    return rows.filter(
      (row) => {

        const date =
          parseDate(
            row.dataset.date
          );

        return Boolean(
          date &&
          date.getFullYear() ===
            year &&
          date.getMonth() ===
            monthIndex
        );
      }
    );
  }


  function updateArchiveRowSelection() {
    rows.forEach(
      (row) => {

        row.classList.toggle(
          "is-selected",
          row ===
            archiveSelectionRow
        );

      }
    );
  }


  function setArchiveSelection(
    row,
    options = {}
  ) {
    if (!row) return;

    const {
      syncDate = true
    } = options;

    archiveSelectionRow =
      row;

    if (syncDate) {

      const date =
        parseDate(
          row.dataset.date
        );

      if (date) {

        currentYear =
          date.getFullYear();

        selectedMonth =
          date.getMonth();
      }
    }

    updateArchiveRowSelection();

    renderArchiveCalendar();
  }


  function filterRows() {
    let visibleCount =
      0;

    rows.forEach(
      (row) => {

        const date =
          parseDate(
            row.dataset.date
          );

        const show =
          Boolean(
            date &&
            date.getFullYear() ===
              currentYear &&
            date.getMonth() ===
              selectedMonth
          );

        row.hidden =
          !show;

        if (show) {
          visibleCount += 1;
        }
      }
    );

    if (monthEmpty) {

      monthEmpty.hidden =
        visibleCount > 0;

      monthEmpty.textContent =
        visibleCount > 0
          ? ""
          : "NO VOICE NOTES FOUND FOR THIS MONTH.";
    }

    if (archiveMonthMeta) {

      archiveMonthMeta.textContent =
        `${visibleCount} ${
          visibleCount === 1
            ? "RECORDING"
            : "RECORDINGS"
        }`;
    }
  }


  function renderArchiveYears() {
    if (!archiveYears) return;

    archiveYears.innerHTML =
      "";

    availableYears.forEach(
      (year) => {

        const yearRows =
          rows.filter(
            (row) => {

              const date =
                parseDate(
                  row.dataset.date
                );

              return Boolean(
                date &&
                date.getFullYear() ===
                  year
              );
            }
          );

        const button =
          document.createElement(
            "button"
          );

        button.type =
          "button";

        button.className =
          "dt-archive-year";

        if (
          year ===
          currentYear
        ) {
          button.classList.add(
            "is-selected"
          );
        }

        const strong =
          document.createElement(
            "strong"
          );

        strong.textContent =
          String(year);

        const meta =
          document.createElement(
            "span"
          );

        meta.textContent =
          `${yearRows.length} ${
            yearRows.length === 1
              ? "LOG"
              : "LOGS"
          }`;

        button.append(
          strong,
          meta
        );

        button.addEventListener(
          "click",
          () => {

            currentYear =
              year;

            const firstRow =
              yearRows[0];

            if (firstRow) {

              const date =
                parseDate(
                  firstRow.dataset.date
                );

              if (date) {
                selectedMonth =
                  date.getMonth();
              }

              archiveSelectionRow =
                firstRow;
            }

            renderArchiveCalendar();
          }
        );

        archiveYears.appendChild(
          button
        );
      }
    );
  }


  function renderArchiveWeekAndDays() {
    if (
      !archiveWeeks ||
      !archiveDays
    ) {
      return;
    }

    archiveWeeks.innerHTML =
      "";

    archiveDays.innerHTML =
      "";

    const monthRows =
      rowsForMonth(
        currentYear,
        selectedMonth
      );

    if (!monthRows.length) {
      return;
    }

    let focusRow =
      archiveSelectionRow;

    let focusDate =
      focusRow
        ? parseDate(
            focusRow.dataset.date
          )
        : null;

    if (
      !focusDate ||
      focusDate.getFullYear() !==
        currentYear ||
      focusDate.getMonth() !==
        selectedMonth
    ) {

      focusRow =
        monthRows[0];

      focusDate =
        parseDate(
          focusRow.dataset.date
        );
    }

    if (!focusDate) return;

    const firstOfMonth =
      new Date(
        currentYear,
        selectedMonth,
        1,
        12,
        0,
        0
      );

    const lastOfMonth =
      new Date(
        currentYear,
        selectedMonth + 1,
        0,
        12,
        0,
        0
      );

    const firstOffset =
      (
        firstOfMonth.getDay() +
        6
      ) % 7;

    const weekCount =
      Math.ceil(
        (
          firstOffset +
          lastOfMonth.getDate()
        ) / 7
      );

    const selectedWeek =
      Math.floor(
        (
          focusDate.getDate() +
          firstOffset -
          1
        ) / 7
      );

    archiveWeeks.style
      .setProperty(
        "--week-count",
        String(
          weekCount
        )
      );

    for (
      let i = 0;
      i < weekCount;
      i += 1
    ) {

      const week =
        document.createElement(
          "span"
        );

      week.className =
        "dt-archive-week";

      if (
        i === selectedWeek
      ) {
        week.classList.add(
          "is-selected"
        );
      }

      week.textContent =
        `WEEK ${i + 1}`;

      archiveWeeks.appendChild(
        week
      );
    }

    const dayOfWeek =
      focusDate.getDay();

    const mondayOffset =
      dayOfWeek === 0
        ? -6
        : 1 - dayOfWeek;

    const weekStart =
      new Date(
        focusDate
      );

    weekStart.setDate(
      focusDate.getDate() +
      mondayOffset
    );

    for (
      let i = 0;
      i < 7;
      i += 1
    ) {

      const date =
        new Date(
          weekStart
        );

      date.setDate(
        weekStart.getDate() +
        i
      );

      const key =
        localDateKey(
          date
        );

      const matchingRow =
        rows.find(
          (row) =>
            row.dataset.date ===
            key
        ) || null;

      const button =
        document.createElement(
          "button"
        );

      button.type =
        "button";

      button.className =
        "dt-archive-day";

      const number =
        document.createElement(
          "strong"
        );

      number.textContent =
        String(
          date.getDate()
        ).padStart(
          2,
          "0"
        );

      const dot =
        document.createElement(
          "i"
        );

      button.append(
        number,
        dot
      );

      if (matchingRow) {

        button.classList.add(
          "has-entry"
        );

        button.addEventListener(
          "click",
          () => {

            setArchiveSelection(
              matchingRow
            );

          }
        );

      } else {

        button.disabled =
          true;
      }

      if (
        archiveSelectionRow &&
        archiveSelectionRow.dataset.date ===
          key
      ) {

        button.classList.add(
          "is-selected"
        );
      }

      archiveDays.appendChild(
        button
      );
    }
  }
    function updateYearButtons() {
    const index =
      availableYears.indexOf(
        currentYear
      );

    if (previousYearButton) {
      previousYearButton.disabled =
        index <= 0;
    }

    if (nextYearButton) {
      nextYearButton.disabled =
        index < 0 ||
        index >=
          availableYears.length - 1;
    }
  }


  function renderArchiveCalendar() {
    if (yearLabel) {
      yearLabel.textContent =
        String(
          currentYear
        );
    }

    if (archiveMonthTitle) {

      archiveMonthTitle.textContent =
        `${fullMonthNames[selectedMonth]} ${currentYear}`;
    }

    renderArchiveYears();

    if (calendar) {

      calendar.innerHTML =
        "";

      monthNames.forEach(
        (
          monthName,
          monthIndex
        ) => {

          const monthRows =
            rowsForMonth(
              currentYear,
              monthIndex
            );

          const button =
            document.createElement(
              "button"
            );

          button.type =
            "button";

          button.className =
            "dt-voice-month";

          if (monthRows.length) {

            button.classList.add(
              "has-entry"
            );
          }

          if (
            monthIndex ===
            selectedMonth
          ) {

            button.classList.add(
              "is-selected"
            );
          }

          const name =
            document.createElement(
              "span"
            );

          name.className =
            "dt-voice-month-name";

          name.textContent =
            monthName;

          const dot =
            document.createElement(
              "span"
            );

          dot.className =
            "dt-voice-month-dot";

          button.append(
            name,
            dot
          );

          if (!monthRows.length) {

            button.disabled =
              true;

          } else {

            button.addEventListener(
              "click",
              () => {

                selectedMonth =
                  monthIndex;

                archiveSelectionRow =
                  monthRows[0];

                renderArchiveCalendar();
              }
            );
          }

          calendar.appendChild(
            button
          );
        }
      );
    }

    filterRows();

    updateArchiveRowSelection();

    renderArchiveWeekAndDays();

    updateYearButtons();
  }


  function changeArchiveYear(direction) {
    const index =
      availableYears.indexOf(
        currentYear
      );

    const targetIndex =
      index +
      direction;

    if (
      targetIndex < 0 ||
      targetIndex >=
        availableYears.length
    ) {
      return;
    }

    currentYear =
      availableYears[
        targetIndex
      ];

    const yearRows =
      rows.filter(
        (row) => {

          const date =
            parseDate(
              row.dataset.date
            );

          return Boolean(
            date &&
            date.getFullYear() ===
              currentYear
          );
        }
      );

    const firstRow =
      yearRows[0];

    if (firstRow) {

      const date =
        parseDate(
          firstRow.dataset.date
        );

      if (date) {
        selectedMonth =
          date.getMonth();
      }

      archiveSelectionRow =
        firstRow;
    }

    renderArchiveCalendar();
  }


  function openArchive() {
    if (!archiveDrawer) return;

    const date =
      parseDate(
        selectedRow.dataset.date
      );

    archiveSelectionRow =
      selectedRow;

    if (date) {

      currentYear =
        date.getFullYear();

      selectedMonth =
        date.getMonth();
    }

    renderArchiveCalendar();

    root.classList.add(
      "is-archive-open"
    );

    document.body.classList.add(
      "dt-archive-lock"
    );

    archiveDrawer.setAttribute(
      "aria-hidden",
      "false"
    );
  }


  function closeArchive() {
    if (!archiveDrawer) return;

    root.classList.remove(
      "is-archive-open"
    );

    document.body.classList.remove(
      "dt-archive-lock"
    );

    archiveDrawer.setAttribute(
      "aria-hidden",
      "true"
    );
  }


  /* =======================================================
     SELECT MAIN LOG
  ======================================================= */

  function selectRow(
    row,
    options = {}
  ) {
    if (!row) return;

    const {
      syncArchive = true
    } = options;

    selectedRow =
      row;

    archiveSelectionRow =
      row;

    if (selectedDate) {

      selectedDate.textContent =
        formatLongDate(
          row.dataset.date
        );
    }

    if (selectedTitle) {

      selectedTitle.textContent =
        row.dataset.title ||
        "";
    }

    if (selectedLog) {

      selectedLog.textContent =
        row.dataset.log ||
        "---";
    }

    syncRecentSelection(
      row
    );

    updateTranscript(
      row
    );

    renderPolaroids(
      row
    );

    loadAudioForRow(
      row
    );

    if (syncArchive) {

      const date =
        parseDate(
          row.dataset.date
        );

      if (date) {

        currentYear =
          date.getFullYear();

        selectedMonth =
          date.getMonth();
      }
    }

    renderArchiveCalendar();

    root.dispatchEvent(
      new CustomEvent(
        "dt:voice-entry-change",
        {
          detail: {
            title:
              row.dataset.title ||
              "",

            audio:
              row.dataset.audio ||
              "",

            date:
              row.dataset.date ||
              ""
          }
        }
      )
    );
  }


  /* =======================================================
     PLAYBACK
  ======================================================= */

  async function playSelectedRow() {
    const source =
      sourceForRow(
        selectedRow
      );

    if (!source) return;

    pauseSiteMusic();

    if (!audio.src) {

      audio.src =
        source;

      audio.load();
    }

    await ensureVoiceAnalyser();

    try {

      await audio.play();

      setPlayingState();

    } catch (error) {

      console.warn(
        "Voice Archive playback failed:",
        error
      );

      setStoppedState();
    }
  }


  async function selectAndPlayRow(row) {
    if (!row) return;

    selectRow(
      row
    );

    if (
      !sourceForRow(
        row
      )
    ) {
      return;
    }

    await playSelectedRow();
  }


  /* =======================================================
     TOP PLAYER CONTROL
  ======================================================= */

  topToggleButton?.addEventListener(
    "click",
    async () => {
      if (topToggleButton.disabled) return;

      if (recorderMode === "playing") {
        playCassetteClick("pause");
        audio.pause();
        setPausedState();
        return;
      }

      playCassetteClick("play");
      await playSelectedRow();
    }
  );


  /* =======================================================
     RECORDER CONTROLS
  ======================================================= */

  playButton?.addEventListener(
    "click",
    async () => {

      hit(
        playButton
      );

      playCassetteClick(
        "play"
      );

      await playSelectedRow();
    }
  );


  stopButton?.addEventListener(
    "click",
    () => {

      hit(
        stopButton
      );

      playCassetteClick(
        "stop"
      );

      stopAudio(
        true
      );
    }
  );


  pauseButton?.addEventListener(
    "click",
    async () => {

      hit(
        pauseButton
      );

      playCassetteClick(
        "pause"
      );

      if (
        recorderMode ===
        "playing"
      ) {

        audio.pause();

        setPausedState();

        return;
      }

      if (
        recorderMode ===
        "paused"
      ) {

        pauseSiteMusic();

        await ensureVoiceAnalyser();

        try {

          await audio.play();

          setPlayingState();

        } catch (error) {

          console.warn(
            "Voice Archive resume failed:",
            error
          );
        }
      }
    }
  );


  rewindButton?.addEventListener(
    "click",
    () => {

      hit(
        rewindButton
      );

      playCassetteClick(
        "rew"
      );

      if (!audio.src) {
        return;
      }

      try {

        audio.currentTime =
          Math.max(
            0,
            audio.currentTime -
            10
          );

        renderRecorderReels();

        syncTimedTranscript(
          audio.currentTime,
          true
        );

      } catch (_) {}
    }
  );


  forwardButton?.addEventListener(
    "click",
    () => {

      hit(
        forwardButton
      );

      playCassetteClick(
        "fwd"
      );

      if (!audio.src) {
        return;
      }

      const endTime =
        Number.isFinite(
          audio.duration
        )
          ? audio.duration
          : audio.currentTime +
            10;

      try {

        audio.currentTime =
          Math.min(
            endTime,
            audio.currentTime +
            10
          );

        renderRecorderReels();

        syncTimedTranscript(
          audio.currentTime,
          true
        );

      } catch (_) {}
    }
  );


  /* =======================================================
     AUDIO EVENTS
  ======================================================= */

  audio.addEventListener(
    "play",
    () => {

      setPlayingState();

      startVoiceWave();

      startRecorderReels();
    }
  );


  audio.addEventListener(
    "pause",
    () => {

      stopVoiceWave();

      stopRecorderReels();
    }
  );


  audio.addEventListener(
    "timeupdate",
    () => {

      if (currentTimeLabel) {

        currentTimeLabel.textContent =
          formatTime(
            audio.currentTime
          );
      }

      syncTimedTranscript(
        audio.currentTime
      );

      if (audio.paused) {
        renderRecorderReels();
      }
    }
  );


  audio.addEventListener(
    "seeked",
    () => {

      renderRecorderReels();

      syncTimedTranscript(
        audio.currentTime,
        true
      );
    }
  );


  audio.addEventListener(
    "loadedmetadata",
    () => {

      if (!selectedRow) {
        return;
      }

      const manualDuration =
        (
          selectedRow.dataset.duration ||
          ""
        ).trim();

      const displayDuration =
        manualDuration ||
        formatTime(
          audio.duration
        );

      if (durationLabel) {

        durationLabel.textContent =
          displayDuration;
      }

      const rowDuration =
        selectedRow.querySelector(
          "[data-row-duration]"
        );

      if (
        rowDuration &&
        !manualDuration
      ) {

        rowDuration.textContent =
          displayDuration;
      }
    }
  );


  audio.addEventListener(
    "ended",
    () => {

      stopAudio(
        true
      );
    }
  );


  audio.addEventListener(
    "error",
    () => {

      console.warn(
        "Voice Archive MP3 could not be loaded:",
        audio.src
      );

      stopVoiceWave();

      stopRecorderReels();

      setStoppedState();
    }
  );


  /* =======================================================
     RECENT TRANSMISSION CLICKS
  ======================================================= */

  recentItems.forEach(
    (item) => {

      item.addEventListener(
        "click",
        async () => {

          const index =
            Number.parseInt(
              item.dataset.rowIndex,
              10
            );

          if (
            !Number.isInteger(
              index
            ) ||
            !rows[index]
          ) {
            return;
          }

          await selectAndPlayRow(
            rows[index]
          );
        }
      );
    }
  );


  recentPrevButton?.addEventListener(
    "click",
    () => {
      recentStrip?.scrollBy({
        left: -260,
        behavior: "smooth"
      });
    }
  );


  recentNextButton?.addEventListener(
    "click",
    () => {
      recentStrip?.scrollBy({
        left: 260,
        behavior: "smooth"
      });
    }
  );


  /* =======================================================
     ARCHIVE ROW CLICKS
  ======================================================= */

  rows.forEach(
    (row) => {

      const selectButton =
        row.querySelector(
          "[data-archive-row-select]"
        );

      selectButton?.addEventListener(
        "click",
        () => {

          setArchiveSelection(
            row
          );

        }
      );
    }
  );


  /* =======================================================
     ARCHIVE DRAWER EVENTS
  ======================================================= */

  archiveOpenButtons.forEach(
    (button) => {

      button.addEventListener(
        "click",
        openArchive
      );

    }
  );


  archiveCloseButton
    ?.addEventListener(
      "click",
      closeArchive
    );


  archiveScrim
    ?.addEventListener(
      "click",
      closeArchive
    );


  previousYearButton
    ?.addEventListener(
      "click",
      () => {

        changeArchiveYear(
          -1
        );

      }
    );


  nextYearButton
    ?.addEventListener(
      "click",
      () => {

        changeArchiveYear(
          1
        );

      }
    );


  archiveTabs.forEach(
    (tab) => {

      tab.addEventListener(
        "click",
        () => {

          archiveTabs.forEach(
            (candidate) =>

              candidate.classList.toggle(
                "is-active",
                candidate === tab
              )
          );

        }
      );
    }
  );


  archiveLoadButton
    ?.addEventListener(
      "click",
      () => {

        if (!archiveSelectionRow) {
          return;
        }

        selectRow(
          archiveSelectionRow
        );

        closeArchive();
      }
    );


  document.addEventListener(
    "keydown",
    (event) => {

      if (
        event.key ===
          "Escape" &&
        root.classList.contains(
          "is-archive-open"
        )
      ) {

        closeArchive();
      }
    }
  );


  /* =======================================================
     INITIALIZE
  ======================================================= */

  updateTranscript(
    selectedRow
  );

  renderPolaroids(
    selectedRow
  );

  loadAudioForRow(
    selectedRow
  );

  syncRecentSelection(
    selectedRow
  );

  archiveSelectionRow =
    selectedRow;

  renderArchiveCalendar();

  settleVoiceWave();

  renderRecorderReels();

})();